import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { 
  ArrowLeftRight, Calendar, Sun, Clock, CheckCircle2, XCircle, 
  User, Users, MessageSquare, ChevronDown
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const RichiesteTurni = () => {
  const { user, token, localeCorrente, isAdmin } = useAuth();
  const canApprova = isAdmin || user?.funzionalita?.includes("approva_richieste");
  
  const [loading, setLoading] = useState(true);
  const [assenze, setAssenze] = useState([]);
  const [cambiTurno, setCambiTurno] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [sostitutoDialog, setSostitutoDialog] = useState(null);
  const [sostitutoId, setSostitutoId] = useState("");
  const [tab, setTab] = useState("tutte"); // tutte, assenze, cambi

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id) return;
    setLoading(true);
    try {
      const [assRes, cambiRes, opRes] = await Promise.all([
        axios.get(`${API}/api/personale/assenze?locale_id=${localeCorrente.id}&stato=pending`, { headers }),
        axios.get(`${API}/api/personale/cambi-turno?locale_id=${localeCorrente.id}&stato=pending`, { headers }),
        axios.get(`${API}/api/utenti?locale_id=${localeCorrente.id}`, { headers }),
      ]);
      setAssenze(assRes.data || []);
      setCambiTurno(cambiRes.data || []);
      setOperatori(opRes.data?.filter(u => u.ruolo !== "admin") || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprovaAssenza = async (id, approva) => {
    try {
      await axios.put(`${API}/api/personale/assenze/${id}/risposta`, {
        approvata: approva,
        nota_risposta: ""
      }, { headers });
      toast.success(approva ? "Assenza approvata" : "Assenza rifiutata");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleGestisciCambio = async (id, azione, sostId) => {
    try {
      let url = `${API}/api/personale/cambi-turno/${id}/gestisci?azione=${azione}`;
      if (sostId) url += `&sostituto_id=${sostId}`;
      await axios.put(url, null, { headers });
      toast.success(azione === "approva" ? "Cambio approvato" : "Cambio rifiutato");
      setSostitutoDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const totale = assenze.length + cambiTurno.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header conteggi */}
      <div className="flex gap-2 px-4">
        {[
          { key: "tutte", label: "Tutte", count: totale },
          { key: "assenze", label: "Ferie/Permessi", count: assenze.length },
          { key: "cambi", label: "Cambi turno", count: cambiTurno.length },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5",
              tab === t.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            )}
          >
            {t.label}
            {t.count > 0 && (
              <span className={cn(
                "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                tab === t.key ? "bg-white/20" : "bg-primary/20 text-primary"
              )}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {totale === 0 && (
        <div className="text-center py-12 px-4">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500/30 mb-3" />
          <p className="text-muted-foreground">Nessuna richiesta in attesa</p>
        </div>
      )}

      {/* ASSENZE */}
      {(tab === "tutte" || tab === "assenze") && assenze.map(a => (
        <div key={a.id} className="mx-4 p-4 rounded-xl border space-y-3" data-testid={`richiesta-assenza-${a.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Sun className="h-4 w-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{a.utente_nome || a.richiedente_nome}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-xs capitalize">{a.tipo_assenza || a.tipo}</Badge>
                <span className="text-xs text-muted-foreground">
                  {a.data_inizio} → {a.data_fine}
                </span>
              </div>
              {a.nota && <p className="text-xs text-muted-foreground mt-1 italic">"{a.nota}"</p>}
            </div>
          </div>
          {canApprova && (
            <div className="flex gap-2">
              <Button size="sm" className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => handleApprovaAssenza(a.id, true)}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Approva
              </Button>
              <Button size="sm" variant="outline" className="flex-1 h-9 text-red-500 border-red-500/30 hover:bg-red-500/10 gap-1" onClick={() => handleApprovaAssenza(a.id, false)}>
                <XCircle className="h-3.5 w-3.5" /> Rifiuta
              </Button>
            </div>
          )}
        </div>
      ))}

      {/* CAMBI TURNO */}
      {(tab === "tutte" || tab === "cambi") && cambiTurno.map(c => (
        <div key={c.id} className="mx-4 p-4 rounded-xl border space-y-3" data-testid={`richiesta-cambio-${c.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <ArrowLeftRight className="h-4 w-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{c.richiedente_nome}</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  {c.tipo === "collega" ? "A collega" : "Al responsabile"}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {c.data} &middot; {c.turno_info}
                </span>
              </div>
              {c.tipo === "collega" && c.collega_nome && (
                <p className="text-xs text-muted-foreground mt-1">
                  Scambio con: <span className="font-medium text-foreground">{c.collega_nome}</span>
                  {c.collega_turno_info && ` (${c.collega_turno_info})`}
                </p>
              )}
              {c.nota && <p className="text-xs text-muted-foreground mt-1 italic">"{c.nota}"</p>}
            </div>
          </div>
          {canApprova && (
            <div className="flex gap-2">
              {c.tipo === "responsabile" ? (
                // Per tipo responsabile, l'admin deve scegliere il sostituto
                <Button 
                  size="sm" 
                  className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                  onClick={() => setSostitutoDialog(c)}
                >
                  <User className="h-3.5 w-3.5" /> Assegna sostituto
                </Button>
              ) : (
                <Button size="sm" className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => handleGestisciCambio(c.id, "approva")}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approva
                </Button>
              )}
              <Button size="sm" variant="outline" className="flex-1 h-9 text-red-500 border-red-500/30 hover:bg-red-500/10 gap-1" onClick={() => handleGestisciCambio(c.id, "rifiuta")}>
                <XCircle className="h-3.5 w-3.5" /> Rifiuta
              </Button>
            </div>
          )}
        </div>
      ))}

      {/* Dialog scelta sostituto */}
      {sostitutoDialog && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setSostitutoDialog(null)}>
          <div className="bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border shadow-xl max-h-[70vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold">Scegli sostituto</h3>
            <p className="text-sm text-muted-foreground">
              {sostitutoDialog.richiedente_nome} chiede cambio per il {sostitutoDialog.data}
            </p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {operatori.filter(op => op.id !== sostitutoDialog.richiedente_id).map(op => (
                <button
                  key={op.id}
                  onClick={() => {
                    handleGestisciCambio(sostitutoDialog.id, "approva", op.id);
                  }}
                  className="w-full p-3 rounded-xl border hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all text-left flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{op.nome || op.email || op.telefono}</p>
                    <p className="text-xs text-muted-foreground capitalize">{op.ruolo}</p>
                  </div>
                </button>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={() => setSostitutoDialog(null)}>Annulla</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichiesteTurni;

/** Versione standalone solo cambi turno (usata dentro FeriePermessi) */
export function CambiTurnoPanel() {
  const { user, token, localeCorrente, isAdmin } = useAuth();
  const canApprova = isAdmin || user?.funzionalita?.includes("approva_richieste");
  const [loading, setLoading] = useState(true);
  const [cambiTurno, setCambiTurno] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [sostitutoDialog, setSostitutoDialog] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id) return;
    setLoading(true);
    try {
      const [ct, ops] = await Promise.all([
        axios.get(`${API}/api/personale/cambi-turno?locale_id=${localeCorrente.id}`, { headers }),
        axios.get(`${API}/api/utenti?locale_id=${localeCorrente.id}`, { headers }),
      ]);
      setCambiTurno(ct.data.filter(c => c.stato === "pendente" || c.stato === "approvato"));
      setOperatori(ops.data);
    } catch {}
    setLoading(false);
  }, [localeCorrente?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGestisciCambio = async (id, azione, sostId) => {
    try {
      let url = `${API}/api/personale/cambi-turno/${id}/gestisci?azione=${azione}`;
      if (sostId) url += `&sostituto_id=${sostId}`;
      await axios.put(url, null, { headers });
      toast.success(azione === "approva" ? "Cambio approvato" : "Cambio rifiutato");
      setSostitutoDialog(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;

  if (cambiTurno.length === 0) return (
    <div className="text-center py-12 px-4">
      <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500/30 mb-3" />
      <p className="text-muted-foreground">Nessuna richiesta di cambio turno</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {cambiTurno.map(c => (
        <div key={c.id} className="p-4 rounded-xl border space-y-3" data-testid={`cambio-${c.id}`}>
          <div className="flex items-start gap-3">
            <div className={cn("w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0", c.stato === "approvato" ? "bg-emerald-500/10" : "bg-blue-500/10")}>
              {c.stato === "approvato" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <ArrowLeftRight className="h-4 w-4 text-blue-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{c.richiedente_nome}</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Badge variant="outline" className={cn("text-xs", c.stato === "approvato" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700")}>
                  {c.stato === "approvato" ? "Approvato" : "Da approvare"}
                </Badge>
                <span className="text-xs text-muted-foreground">{c.data} &middot; {c.turno_info}</span>
              </div>
              {c.tipo === "collega" && c.collega_nome && (
                <p className="text-xs text-muted-foreground mt-1">Scambio con: <span className="font-medium text-foreground">{c.collega_nome}</span></p>
              )}
              {c.nota && <p className="text-xs text-muted-foreground mt-1 italic">"{c.nota}"</p>}
            </div>
          </div>
          {canApprova && c.stato === "pendente" && (
            <div className="flex gap-2">
              {c.tipo === "responsabile" ? (
                <Button size="sm" className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => setSostitutoDialog(c)}>
                  <User className="h-3.5 w-3.5" /> Assegna sostituto
                </Button>
              ) : (
                <Button size="sm" className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => handleGestisciCambio(c.id, "approva")}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approva
                </Button>
              )}
              <Button size="sm" variant="outline" className="flex-1 h-9 text-red-500 border-red-500/30 hover:bg-red-500/10 gap-1" onClick={() => handleGestisciCambio(c.id, "rifiuta")}>
                <XCircle className="h-3.5 w-3.5" /> Rifiuta
              </Button>
            </div>
          )}
        </div>
      ))}
      {sostitutoDialog && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setSostitutoDialog(null)}>
          <div className="bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border shadow-xl max-h-[70vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold">Scegli sostituto</h3>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {operatori.filter(op => op.id !== sostitutoDialog.richiedente_id).map(op => (
                <button key={op.id} onClick={() => handleGestisciCambio(sostitutoDialog.id, "approva", op.id)}
                  className="w-full p-3 rounded-xl border hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all text-left flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><User className="h-4 w-4 text-muted-foreground" /></div>
                  <div><p className="font-medium text-sm">{op.nome || op.email || op.telefono}</p><p className="text-xs text-muted-foreground capitalize">{op.ruolo}</p></div>
                </button>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={() => setSostitutoDialog(null)}>Annulla</Button>
          </div>
        </div>
      )}
    </div>
  );
}
